import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAppPinLock } from '@/contexts/AppPinLockContext';
import {
  clearMessengerAppPresence,
  heartbeatMessengerAppPresence,
  MESSENGER_HEARTBEAT_MS,
} from '@/services/messengerAppPresenceFirestore';

/**
 * Пока пользователь на `/app/*`, вкладка видима и PIN разблокирован — heartbeat в Firestore.
 * На экране PIN пользователь недоступен (не «онлайн»).
 */
export function MessengerAppPresenceHeartbeat() {
  const { currentUser } = useAuth() as { currentUser: { uid: string } | null };
  const location = useLocation();
  const uid = currentUser?.uid;
  const inMessengerApp = location.pathname.startsWith('/app');
  const { blocksCalls } = useAppPinLock();
  const available = inMessengerApp && !blocksCalls;

  useEffect(() => {
    if (!uid || !available) {
      if (uid) {
        void clearMessengerAppPresence(uid);
      }
      return;
    }

    const sendHeartbeat = () => {
      void heartbeatMessengerAppPresence(uid).catch(() => {
        /* permission-denied/сеть: не роняем UI и не спамим Uncaught в консоль */
      });
    };

    const tick = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
      }
    };
    tick();
    const intervalId = window.setInterval(tick, MESSENGER_HEARTBEAT_MS);

    /** В фоне heartbeat не шлём — статус станет «не онлайн», но `updatedAt` в Firestore остаётся для «был недавно». */
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
      void clearMessengerAppPresence(uid);
    };
  }, [uid, available]);

  return null;
}
